import FormTitle from "../../../components/FormTitle/FormTitle";
import { useEffect, useState } from "react";
import { useListManager } from "../../../contexts/list.manager";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import Form from "../../../components/Form/Form";
import Input from "../../../components/Input/Input";
import TextAreaInput from "../../../components/TextAreaInput/TextAreaInput";
import LicenseService from "../../../services/LicensesService";
import { MdOutlineCategory } from "react-icons/md";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { BiDollar } from "react-icons/bi";
import { AnimatedTabbar, AnimatedTabbarItem } from "../../../components/AnimatedTabbar/AnimatedTabbar";
import { BillingPeriod, BillingPeriodText } from "../../../constants/billing";
import { formatMoney, parseMoney } from "../../../utils/number";
import Button from "../../../components/Button/Button";
import { FaArrowTrendUp } from "react-icons/fa6";
import ModalComponent from "../../../components/Modal/ModalComponent";
import { PriceInput } from "../../../components/PriceInput/PriceInput";
import { useTranslation } from "react-i18next";
import { IoAddCircleOutline } from "react-icons/io5";
import { CurrentPrice } from "../../../components/CurrentPrice/CurrentPrice";
import { PricesByPeriod } from "../../../components/PricesByPeriod/PricesByPeriod";
import Checkbox from "../../../components/Checkbox/Checkbox";

export function FormLicenseType({ title, ...props }) {
   const [code, setCode] = useState(props.licenseType?.code);
   const [name, setName] = useState(props.licenseType?.name);
   const [enabled, setEnabled] = useState(props.licenseType?.enabled);
   const [system, setSystem] = useState(props.licenseType?.system);
   const [description, setDescription] = useState(props.licenseType?.description);
   const [metadata, setMetadata] = useState(props.licenseType?.metadata);
   const [currentPrices, setCurrentPrices] = useState(props.licenseType?.currentPrices ?? {});
   const [lastPrices, setLastPrices] = useState(props.licenseType?.lastPrices ?? {});
   const [features, setFeatures] = useState([]);

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isLoadingFeatures, setIsLoadingFeatures] = useState(true);
   const [featuresData, setFeaturesData] = useState([]);

   const [showLoader, setShowLoader] = useState(false);
   const { closeForm } = useListManager();
   const navigate = useNavigate();
   const [activeTab, setActiveTab] = useState(BillingPeriod.MONTHLY);

   const [showAdjustModal, setShowAdjustModal] = useState(false);
   const [adjustState, setAdjustState] = useState({
      lastPrice: null,
      newAmount: null,
      startDate: null,
   });

   const { t, i18n } = useTranslation();

   async function fetchFeatures() {
      setIsLoadingFeatures(true);
      try {
         const response = await LicenseService.getFeatures();
         setFeaturesData(response.data);
      } catch { }
      setIsLoadingFeatures(false)
   }

   useEffect(() => {
      fetchFeatures();

      setCode(props.licenseType?.code || "");
      setName(props.licenseType?.name || "");
      setEnabled(props.licenseType?.enabled || false);
      setSystem(props.licenseType?.system || false);
      setDescription(props.licenseType?.description || "");
      setMetadata(props.licenseType?.metadata || "");
      setCurrentPrices(props.licenseType?.currentPrices);
      setLastPrices(props.licenseType?.lastPrices);
      setFeatures(props.licenseType?.features ?? []);
   }, [props.licenseType])

   function buildPayload() {
      const adjustments = {};

      Object.entries(currentPrices).map(([period, price]) => {
         if (!price.id) {
            adjustments[period] = price;
         }
      });

      const payload = {
         code,
         name,
         enabled: enabled ?? false,
         system: system ?? false,
         description,
         metadata,
         featureExternalIds: features?.map(f => f.externalId),
      };

      const currentPricesEntries = Object.entries(currentPrices);
      const pricesToAdjust = {};

      if (currentPricesEntries?.find(([_, price]) => !price.id)) {
         for (const [period, price] of currentPricesEntries) {
            if (!price.id) {
               pricesToAdjust[period] = price;
            }
         }
      }

      if (Object.keys(pricesToAdjust).length > 0) {
         payload.currentPrices = pricesToAdjust;
      }

      return payload;
   }

   async function handleSubmit(event) {
      event.preventDefault();
      if (props.type === "edicao") {
         setIsSubmitting(true);
         await handleEdit(event);
         setIsSubmitting(false);
         return;
      }
      setIsSubmitting(true);
      const payload = buildPayload();
      const response = await LicenseService.createType(payload);
      if (!response) return;
      if (response.data.id) navigate("/licenses/types", {
         state: { refresh: true }
      });
      setIsSubmitting(false);
   }

   async function handleEdit(event) {
      event.preventDefault();
      const payload = buildPayload();
      const response = await LicenseService.updateType(
         props.licenseType.id,
         payload
      );
      closeForm();
      if (response.data.id) navigate("/licenses/types");
   }

   async function handleDelete() {
      if (!props.licenseType) return;
      await LicenseService.deleteType(props.licenseType.id);
      navigate("/licenses/types", {
         state: { deletedId: props.licenseType.id },
      });
   }

   const handleClickEditButton = () => {
      if (!props.licenseType) return;
      navigate(`/licenses/types/${props.licenseType.externalId}`, { state: props.licenseType });
   };

   function openAdjustModal(period, lastPrice = null) {
      setShowAdjustModal(true);
      setAdjustState({
         period,
         lastPrice,
         newAmount: lastPrice?.amount,
         startDate: lastPrice?.startDate,
      })
   }

   function closeAdjustModal(lastPrice) {
      setShowAdjustModal(false);
      setAdjustState({
         lastPrice: null,
         newAmount: null,
      })
   }

   return (
      <>
         <FormTitle
            type={props.type}
            icon={<MdOutlineCategory size={20} />}
            tituloConsulta={props.licenseType?.name || null}
            tituloCadastro={title || "Cadastro de Tipos de Licença"}
            backRoute={"/licenses/types"}
         />
         {showLoader ? (
            <Loader />
         ) : (
            <Form
               handleSubmit={handleSubmit}
               handleClickEdit={handleClickEditButton}
               type={props.type}
               handleDelete={handleDelete}
               navigateCancelUrl="/licenses/types"
               isSubmitting={isSubmitting}
            >
               <div className={props.type === 'consulta' ? `w-full` : 'grid-2'}>
                  <Input
                     name="name"
                     id="name"
                     labelText="Nome *"
                     value={name}
                     onChange={({ target }) => setName(target.value)}
                     disabled={props.type === "consulta" || isSubmitting}
                     required={true}
                  />
                  <Input
                     name="code"
                     id="code"
                     labelText="Código *"
                     value={code}
                     onChange={({ target }) => setCode(target.value)}
                     disabled={props.type === "consulta" || isSubmitting}
                     required={true}
                  />
               </div>
               <TextAreaInput
                  name={"description"}
                  id="description"
                  labelText={"Informações extras"}
                  onChange={({ target }) => setDescription(target.value)}
                  value={description}
                  hidden={props.type === "consulta" || isSubmitting}
                  rows={3}
               />
               <div className={`flex ${props.type === 'consulta' ? 'flex-col' : 'flex-row'} justify-start w-full gap-5!`}>
                  <ToggleButton
                     label="Habilitado"
                     defaultChecked={enabled}
                     onChange={({ target }) => setEnabled(target.checked)}
                     required={false}
                     disabled={props.type === "consulta" || isSubmitting}
                     tooltipVisible={true}
                  />
                  <ToggleButton
                     label="Padrão do sistema"
                     defaultChecked={system}
                     onChange={({ target }) => setSystem(target.checked)}
                     required={false}
                     disabled={props.type !== "cadastro" || isSubmitting}
                     tooltipVisible={true}
                  />
               </div>

               <div className="flex flex-col w-full justify-start mt-1.5!">
                  <p className="text-left text-(--textColor) text-[14px]! mb-1.5!">Funcionalidades permitidas</p>
                  {isLoadingFeatures ? (
                     <div className="w-fit flex flex-wrap flex-row gap-4">
                        <div className="w-[100px] h-[16px] rounded-full mt-[7px]! shimmer"></div>
                        <div className="w-[80px] h-[16px] rounded-full mt-[7px]! shimmer"></div>
                        <div className="w-[120px] h-[16px] rounded-full mt-[7px]! shimmer"></div>
                        <div className="w-[100px] h-[16px] rounded-full mt-[7px]! shimmer"></div>
                     </div>
                  ) : (
                     <div className={`flex justify-start ${props.type !== 'consulta' ? 'flex-wrap gap-6' : 'flex-col gap-2'}`}>
                        {featuresData?.map(feature => (
                           <Checkbox
                              key={feature.id}
                              labelText={`${feature.name}`}
                              name={`feature${feature.id}`}
                              className="w-fit! inline-flex items-center flex-row-reverse gap-3"
                              defaultChecked={
                                 features?.find(f => f.id === feature.id) ||
                                 false
                              }
                              checked={
                                 features?.find(f => f.id === feature.id) ||
                                 false
                              }
                              onChange={({ target }) => {
                                 console.log(target.checked);
                                 if (!target.checked) {
                                    setFeatures(prevFeatures => {
                                       console.log(prevFeatures);
                                       return prevFeatures.filter(f => f.id !== feature.id);
                                    });
                                 } else if (!features?.find(f => f.id === feature.id)) {
                                    setFeatures(prevFeatures => ([
                                       ...prevFeatures,
                                       feature
                                    ]));
                                 }
                              }}
                              required={false}
                              disabled={props.type === "consulta" || isSubmitting}
                           />
                        ))}
                     </div>
                  )}
               </div>

               {props.type !== 'consulta' && (
                  <>
                     <div className="w-full mt-5! mb-1.5!" style={{ gap: 25 }}>
                        <h1 className="flex flex-row items-center gap-2! font-semibold! text-[1rem]! mb-2! text-left w-full text-(--textColor)">
                           <BiDollar size={19} />
                           Preços por período
                        </h1>
                        <AnimatedTabbar
                           value={activeTab}
                           onChange={setActiveTab}
                        >
                           {Object.values(BillingPeriod).map(period => (
                              <AnimatedTabbarItem name={period}>
                                 {BillingPeriodText[period]}
                              </AnimatedTabbarItem>
                           ))}
                        </AnimatedTabbar>
                     </div>
                     {Object.values(BillingPeriod).filter(period => activeTab === period).map(period => (
                        <>
                           <div className="w-full text-left text-(--textColor)">
                              <CurrentPrice
                                 currentPrices={currentPrices}
                                 period={period}
                              />
                              <Button
                                 label={props.type === 'cadastro' ? "Configurar preço" : "Ajustar preço"}
                                 icon={props.type === 'cadastro' ? <IoAddCircleOutline size={22} /> : <FaArrowTrendUp />}
                                 onClick={e => openAdjustModal(period, currentPrices?.[period])}
                                 className="button-link mt-3!"
                                 disabled={isSubmitting}
                              />
                           </div>
                           {props.type === 'edicao' && props.licenseType && (
                              <PricesByPeriod period={period} resourceId={props.licenseType.id} resourceType="type" />
                           )}
                        </>
                     ))}
                  </>
               )}
            </Form>
         )}
         <ModalComponent
            modalIsOpen={showAdjustModal}
            handleConfirm={() => {
               setLastPrices(prev => ({
                  ...prev,
                  [adjustState.period]: currentPrices?.[adjustState.period]
               }));
               setCurrentPrices(prev => ({
                  ...prev,
                  [adjustState.period]: {
                     amount: adjustState.newAmount,
                     period: adjustState.period,
                     startDate: adjustState.startDate,
                  }
               }));
               closeAdjustModal();
            }}
            handleCancel={() => closeAdjustModal()}
            modalText={props.type === 'edicao' ? "Ajustar preço" : "Configurar preço"}
            buttonLabel1={"Confirmar"}
         >
            <div className="w-[420px] max-w-dvw flex flex-col gap-3">
               <PriceInput
                  name="amount"
                  id="amount"
                  labelText={props.type === 'edicao' ? "Novo valor" : 'Valor'}
                  value={formatMoney(adjustState.newAmount ?? 0)}
                  onChange={({ target }) =>
                     setAdjustState(prev => ({ ...prev, newAmount: Number(target.value) || 0 }))
                  }
                  required={true}
                  tooltipText={props.type === 'edicao' ? `Novo valor ajustado para o período ${BillingPeriodText[adjustState.period]}.` : `Valor para o período ${BillingPeriodText[adjustState.period]}.`}
                  tooltipVisible={true}
               />
               <Input
                  type="date"
                  labelText="Data de Inicio"
                  value={adjustState.startDate}
                  onChange={({ target }) =>
                     setAdjustState(prev => ({ ...prev, startDate: target.value }))
                  }
                  required={false}
                  disabled={props.type === "consulta"}
                  tooltipText={props.type === 'edicao' ? "Inicio da vigência do ajuste" : "Inicio da vigência"}
                  tooltipVisible={true}
               />
            </div>
         </ModalComponent>
      </>
   );
}